import React, { useState, useEffect } from 'react';

const PaymentScheduleBuilder = ({ payments = [], totalAmount = 0, onPaymentsChange }) => {
  const [paymentSchedule, setPaymentSchedule] = useState([]);

  useEffect(() => {
    if (payments.length > 0) {
      setPaymentSchedule(payments);
    } else if (totalAmount > 0) {
      // Create default single payment only if no existing payments provided
      setPaymentSchedule(prev => {
        // Don't override if preset was already loaded
        if (prev.length > 0) return prev;
        return [{
          id: Date.now(),
          payment_number: 1,
          description: 'Full Payment',
          amount: totalAmount,
          due_type: 'date',
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
          milestone: '',
          notes: ''
        }];
      });
    }
  }, [payments, totalAmount]);

  useEffect(() => {
    onPaymentsChange(paymentSchedule);
  }, [paymentSchedule, onPaymentsChange]);

  // Auto-adjust payments when total changes to keep them in sync
  useEffect(() => {
    if (paymentSchedule.length > 0 && totalAmount > 0) {
      const currentScheduled = getTotalScheduled();
      const difference = Math.abs(totalAmount - currentScheduled);
      // Only auto-adjust if the difference is significant (more than $0.01)
      // and we're not already in the middle of an adjustment
      if (difference > 0.01) {
        // Use a timeout to prevent rapid adjustments and potential loops
        const timeoutId = setTimeout(() => {
          autoAdjustPayments();
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [totalAmount]);

  const addPayment = () => {
    const newPayment = {
      id: Date.now(),
      payment_number: paymentSchedule.length + 1,
      description: `Payment ${paymentSchedule.length + 1}`,
      amount: 0,
      due_type: 'date',
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      milestone: '',
      notes: ''
    };
    setPaymentSchedule([...paymentSchedule, newPayment]);
  };

  const removePayment = (paymentId) => {
    const filtered = paymentSchedule.filter(p => p.id !== paymentId);
    // Renumber payments
    const renumbered = filtered.map((payment, index) => ({
      ...payment,
      payment_number: index + 1
    }));
    setPaymentSchedule(renumbered);
  };

  const updatePayment = (paymentId, field, value) => {
    setPaymentSchedule(prev => prev.map(payment => 
      payment.id === paymentId 
        ? { ...payment, [field]: value }
        : payment
    ));
  };

  const getTotalScheduled = () => {
    return paymentSchedule.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
  };

  const getBalanceStatus = () => {
    const scheduled = getTotalScheduled();
    const difference = totalAmount - scheduled;
    
    if (Math.abs(difference) < 0.01) return { status: 'balanced', message: 'Payments match total' };
    if (difference > 0) return { status: 'under', message: `$${difference.toFixed(2)} remaining` };
    return { status: 'over', message: `$${Math.abs(difference).toFixed(2)} over total` };
  };

  const autoAdjustPayments = () => {
    if (paymentSchedule.length === 0 || totalAmount <= 0) return;

    // Calculate current total scheduled
    const currentScheduled = getTotalScheduled();
    
    if (Math.abs(totalAmount - currentScheduled) < 0.01) return; // Already balanced

    // Create proportionally adjusted payments
    let runningTotal = 0;
    const adjustedSchedule = paymentSchedule.map((payment, index) => {
      const originalAmount = parseFloat(payment.amount || 0);
      
      // For the last payment, assign the remaining amount to ensure exact total
      if (index === paymentSchedule.length - 1) {
        const lastPaymentAmount = totalAmount - runningTotal;
        return {
          ...payment,
          amount: Math.round(lastPaymentAmount * 100) / 100
        };
      } else {
        // Proportionally adjust other payments
        const proportion = currentScheduled > 0 ? originalAmount / currentScheduled : 1 / paymentSchedule.length;
        const newAmount = Math.round((proportion * totalAmount) * 100) / 100;
        runningTotal += newAmount;
        
        return {
          ...payment,
          amount: newAmount
        };
      }
    });

    setPaymentSchedule(adjustedSchedule);
  };

  const loadPreset = (preset) => {
    if (totalAmount <= 0) {
      alert('Please set an invoice total before using payment presets.');
      return;
    }

    let newSchedule = [];
    const today = new Date();
    const baseTimestamp = Date.now();
    
    switch (preset) {
      case 'full':
        newSchedule = [{
          id: baseTimestamp,
          payment_number: 1,
          description: 'Full Payment',
          amount: totalAmount,
          due_type: 'date',
          due_date: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          milestone: '',
          notes: 'Payment due in full'
        }];
        break;
      case 'half-half':
        const half = Math.round((totalAmount / 2) * 100) / 100; // Round to 2 decimal places
        newSchedule = [
          {
            id: baseTimestamp,
            payment_number: 1,
            description: 'Deposit (50%)',
            amount: half,
            due_type: 'date',
            due_date: today.toISOString().split('T')[0],
            milestone: '',
            notes: '50% deposit to start work'
          },
          {
            id: baseTimestamp + 1000,
            payment_number: 2,
            description: 'Final Payment (50%)',
            amount: totalAmount - half, // Ensure exact total
            due_type: 'milestone',
            due_date: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            milestone: 'Project Completion',
            notes: 'Final payment upon completion'
          }
        ];
        break;
      case 'thirds':
        const third = Math.round((totalAmount / 3) * 100) / 100; // Round to 2 decimal places
        newSchedule = [
          {
            id: baseTimestamp,
            payment_number: 1,
            description: 'Deposit (33%)',
            amount: third,
            due_type: 'date',
            due_date: today.toISOString().split('T')[0],
            milestone: '',
            notes: 'Deposit to start work'
          },
          {
            id: baseTimestamp + 1000,
            payment_number: 2,
            description: 'Progress Payment (33%)',
            amount: third,
            due_type: 'milestone',
            due_date: new Date(today.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            milestone: '50% Work Complete',
            notes: 'Payment at project midpoint'
          },
          {
            id: baseTimestamp + 2000,
            payment_number: 3,
            description: 'Final Payment (34%)',
            amount: totalAmount - (third * 2), // Ensure exact total
            due_type: 'milestone',
            due_date: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            milestone: 'Project Completion',
            notes: 'Final payment upon completion'
          }
        ];
        break;
      default:
        return;
    }
    
    // Force update with new schedule
    setPaymentSchedule(newSchedule);
  };

  const balanceStatus = getBalanceStatus();

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Payment Schedule</h3>
        <div className="text-right">
          <div className="text-sm text-gray-600">
            Invoice Total: <span className="font-semibold">${totalAmount.toFixed(2)}</span>
          </div>
          <div className="text-sm text-gray-600">
            Scheduled: <span className="font-semibold">${getTotalScheduled().toFixed(2)}</span>
          </div>
          <div className={`text-sm font-medium ${
            balanceStatus.status === 'balanced' ? 'text-green-600' : 
            balanceStatus.status === 'under' ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {balanceStatus.message}
          </div>
        </div>
      </div>

      {/* Preset Options */}
      <div className="mb-4 p-3 bg-white rounded border">
        <div className="text-sm font-medium text-gray-700 mb-2">Quick Presets:</div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => loadPreset('full')}
            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
          >
            Net 30 (Full)
          </button>
          <button
            type="button"
            onClick={() => loadPreset('half-half')}
            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
          >
            50% / 50%
          </button>
          <button
            type="button"
            onClick={() => loadPreset('thirds')}
            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
          >
            33% / 33% / 34%
          </button>
          {balanceStatus.status !== 'balanced' && (
            <button
              type="button"
              onClick={autoAdjustPayments}
              className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded border border-blue-300 font-medium"
            >
              Auto-Adjust to Total
            </button>
          )}
        </div>
      </div>

      {/* Balance Status Alert */}
      {balanceStatus.status !== 'balanced' && (
        <div className={`mb-4 p-3 rounded border ${
          balanceStatus.status === 'under' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 
          'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="font-medium">{balanceStatus.message}</span>
            </div>
            <button
              type="button"
              onClick={autoAdjustPayments}
              className="px-2 py-1 text-xs bg-white hover:bg-gray-50 rounded border font-medium"
            >
              Fix Now
            </button>
          </div>
        </div>
      )}

      {/* Payment Schedule Items */}
      <div className="space-y-4">
        {paymentSchedule.map((payment, index) => (
          <div key={payment.id} className="bg-white p-4 rounded border border-gray-200">
            <div className="flex justify-between items-start mb-3">
              <h4 className="text-md font-medium text-gray-900">
                Payment {payment.payment_number}
              </h4>
              {paymentSchedule.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePayment(payment.id)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={payment.description}
                  onChange={(e) => updatePayment(payment.id, 'description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Payment description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={payment.amount}
                  onChange={(e) => updatePayment(payment.id, 'amount', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Type
                </label>
                <select
                  value={payment.due_type}
                  onChange={(e) => updatePayment(payment.id, 'due_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="date">Fixed Date</option>
                  <option value="milestone">Milestone</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {payment.due_type === 'milestone' ? 'Estimated Date' : 'Due Date'}
                </label>
                <input
                  type="date"
                  value={payment.due_date}
                  onChange={(e) => updatePayment(payment.id, 'due_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {payment.due_type === 'milestone' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Milestone
                  </label>
                  <select
                    value={payment.milestone}
                    onChange={(e) => updatePayment(payment.id, 'milestone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select milestone...</option>
                    <option value="Project Start">Project Start</option>
                    <option value="Material Delivery">Material Delivery</option>
                    <option value="25% Work Complete">25% Work Complete</option>
                    <option value="50% Work Complete">50% Work Complete</option>
                    <option value="75% Work Complete">75% Work Complete</option>
                    <option value="Project Completion">Project Completion</option>
                    <option value="Final Inspection">Final Inspection</option>
                    <option value="Customer Approval">Customer Approval</option>
                    <option value="Custom">Custom (type below)</option>
                  </select>
                  {payment.milestone === 'Custom' && (
                    <input
                      type="text"
                      placeholder="Enter custom milestone..."
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      onChange={(e) => updatePayment(payment.id, 'milestone', e.target.value)}
                    />
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  rows="2"
                  value={payment.notes}
                  onChange={(e) => updatePayment(payment.id, 'notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Additional notes for this payment..."
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={addPayment}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          + Add Payment
        </button>
      </div>

      {balanceStatus.status !== 'balanced' && (
        <div className={`mt-4 p-3 rounded border ${
          balanceStatus.status === 'under' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 
          'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="text-sm font-medium">
            ⚠️ Payment schedule total doesn't match invoice total
          </div>
          <div className="text-sm">
            {balanceStatus.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentScheduleBuilder;